import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { 
  FileText, 
  Download, 
  BarChart3, 
  PieChart as PieChartIcon, 
  LineChart as LineChartIcon,
  Monitor,
  Laptop,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Users,
  Server,
  Smartphone,
  Tablet,
  Apple,
  Clock,
  RefreshCcw,
  History,
  Cpu,
  HardDrive,
  Database,
  Activity,
  Filter,
  Search,
  X
} from 'lucide-react';
import { apiService } from '../services/api';
import DevicePicker from './DevicePicker';

interface DefaultReportViewProps {
  reportId: string;
  reportName: string;
  description: string;
}

type SeverityLevel = 'Critical' | 'High' | 'Medium' | 'Low';

interface DeviceSummaryReportData {
  type: 'device-summary';
  summary: {
    totalDevices: number;
    activeDevices: number;
    laptops: number;
    desktops: number;
    vms?: number;
  };
  platformDistribution: Array<{ name: string; value: number; color: string }>;
  statusBreakdown: Array<{ name: string; value: number; color: string }>;
  departmentDistribution: Array<{ department: string; devices: number; compliance: number }>;
  recentActivity: Array<{ date: string; enrolled: number; removed: number }>;
}

interface ComplianceStatusReportData {
  type: 'compliance-status';
  overallCompliance: number;
  complianceByPolicy: Array<{ policy: string; compliant: number; nonCompliant: number; complianceRate: number }>;
  complianceByPlatform: Array<{ platform: string; total: number; compliant: number; rate: number }>;
  complianceTrend: Array<{ month: string; rate: number }>;
  violations: Array<{ severity: SeverityLevel; count: number }>;
}

interface PlatformDistributionReportData {
  type: 'platform-distribution';
  platformCounts: Array<{ platform: string; count: number; percentage: number; color: string }>;
  osVersions: Record<string, Array<{ version: string; count: number }>>;
  deviceTypes: Array<{ type: string; count: number }>;
  growthTrend: Array<{ month: string; windows: number; linux: number; macos: number; android: number; ios: number }>;
}

interface SecurityAlertsReportData {
  type: 'security-alerts';
  totalAlerts: number;
  criticalAlerts: number;
  highAlerts: number;
  mediumAlerts: number;
  lowAlerts: number;
  alertTypes: Array<{ type: string; count: number; severity: SeverityLevel }>;
  alertsByPlatform: Array<{ platform: string; alerts: number; critical: number; high: number; medium: number; low: number }>;
  alertTrend: Array<{ date: string; count: number }>;
  topAffectedDevices: Array<{ device: string; alerts: number; severity: Exclude<SeverityLevel, 'Low'> }>;
}

interface DeploymentStatusReportData {
  type: 'deployment-status';
  totalDeployments: number;
  activeDeployments: number;
  completedDeployments: number;
  failedDeployments: number;
  deploymentByType: Array<{ type: string; total: number; success: number; failed: number }>;
  deploymentByStatus: Array<{ status: string; count: number; color: string }>;
  recentDeployments: Array<{ name: string; target: string; status: 'Completed' | 'In Progress' | 'Failed'; progress: number; date: string }>;
  deploymentTrend: Array<{ week: string; applications: number; policies: number; scripts: number }>;
  successRate: number;
}

interface HardwareInventoryRecord {
  deviceId: string;
  deviceName?: string | null;
  serialNumber?: string | null;
  model?: string | null;
  osVersion?: string | null;
  cpu?: string | null;
  memoryGb?: number | null;
  storageGb?: number | null;
  batteryHealth?: string | null;
  lastSync?: string | null;
  collectedBy?: string | null;
}

interface HardwareInventoryReportData {
  type: 'hardware-inventory';
  summary: {
    devicesCovered: number;
    avgMemoryGb: number;
    avgStorageGb: number;
  };
  rows: HardwareInventoryRecord[];
}

interface SoftwareInventoryRow {
  deviceId: string;
  deviceName?: string | null;
  serialNumber?: string | null;
  owner?: string | null;
  name: string;
  version?: string | null;
  publisher?: string | null;
  sizeMb?: number | null;
  metered?: boolean;
  lastSync?: string | null;
}

interface SoftwareInventoryReportData {
  type: 'software-inventory';
  summary: {
    applications: number;
    totalFootprintMb: number;
    meteredApps: number;
  };
  rows: SoftwareInventoryRow[];
}

interface SoftwareMeteringRow {
  deviceId: string;
  deviceName?: string | null;
  serialNumber?: string | null;
  application: string;
  usageMinutes: number;
  lastMetering?: string | null;
}

interface SoftwareMeteringReportData {
  type: 'software-metering';
  summary: {
    trackedDevices: number;
    totalUsageMinutes: number;
  };
  rows: SoftwareMeteringRow[];
}

interface ActivityTrackingReportData {
  type: 'activity-tracking';
  summary: {
    unique_users: number;
    unique_devices: number;
    total_activities: number;
    total_time_seconds: number;
    productive_time_seconds: number;
    unique_domains: number;
    unique_apps: number;
  };
  activities: Array<{
    id: string;
    device_id: string;
    device_name: string;
    serial_number?: string | null;
    user_id: string;
    app_name: string;
    window_title?: string;
    url?: string;
    domain?: string;
    start_time: string;
    duration_seconds: number;
    is_productive?: boolean;
    url_category?: string;
    productivity_score?: number;
  }>;
  top_apps: Array<{
    app_name: string;
    activity_count: number;
    total_time_seconds: number;
  }>;
  top_urls: Array<{
    domain: string;
    activity_count: number;
    total_time_seconds: number;
    category?: string;
  }>;
  unique_users: string[];
  time_range: {
    start: string;
    end: string;
    hours: number;
  };
}

type ReportData =
  | DeviceSummaryReportData
  | ComplianceStatusReportData
  | PlatformDistributionReportData
  | SecurityAlertsReportData
  | DeploymentStatusReportData
  | HardwareInventoryReportData
  | SoftwareInventoryReportData
  | SoftwareMeteringReportData
  | ActivityTrackingReportData
  | null;

const isDeviceSummaryData = (data: ReportData): data is DeviceSummaryReportData =>
  data !== null && data.type === 'device-summary';

const isComplianceStatusData = (data: ReportData): data is ComplianceStatusReportData =>
  data !== null && data.type === 'compliance-status';

const isPlatformDistributionData = (data: ReportData): data is PlatformDistributionReportData =>
  data !== null && data.type === 'platform-distribution';

const isSecurityAlertsData = (data: ReportData): data is SecurityAlertsReportData =>
  data !== null && data.type === 'security-alerts';

const isDeploymentStatusData = (data: ReportData): data is DeploymentStatusReportData =>
  data !== null && data.type === 'deployment-status';

const isHardwareInventoryData = (data: ReportData): data is HardwareInventoryReportData =>
  data !== null && data.type === 'hardware-inventory';

const isSoftwareInventoryData = (data: ReportData): data is SoftwareInventoryReportData =>
  data !== null && data.type === 'software-inventory';

const isSoftwareMeteringData = (data: ReportData): data is SoftwareMeteringReportData =>
  data !== null && data.type === 'software-metering';

const isActivityTrackingData = (data: ReportData): data is ActivityTrackingReportData =>
  data !== null && data.type === 'activity-tracking';

const DefaultReportView = ({ reportId, reportName, description }: DefaultReportViewProps) => {
  // Filter state for hardware/software inventory
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [osFilter, setOsFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Filter state for activity tracking
  const [userFilter, setUserFilter] = useState<string>('all');
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [hoursFilter, setHoursFilter] = useState<number>(24);

  // Mock data generators for each report type
  const fallbackData = useMemo<ReportData>(() => {
    switch (reportId) {
      case 'device-summary':
        return {
          type: 'device-summary',
          summary: {
            totalDevices: 1247,
            activeDevices: 1189,
            laptops: 234,
            desktops: 856,
            vms: 157
          },
          platformDistribution: [
            { name: 'Windows', value: 542, color: '#0078D4' },
            { name: 'Linux', value: 312, color: '#FCC624' },
            { name: 'macOS', value: 198, color: '#000000' },
            { name: 'Android', value: 145, color: '#3DDC84' },
            { name: 'iOS', value: 50, color: '#000000' }
          ],
          statusBreakdown: [
            { name: 'Online', value: 1189, color: '#10B981' },
            { name: 'Offline', value: 58, color: '#EF4444' }
          ],
          departmentDistribution: [
            { department: 'IT', devices: 245, compliance: 92 },
            { department: 'Engineering', devices: 189, compliance: 88 },
            { department: 'Sales', devices: 156, compliance: 95 },
            { department: 'Marketing', devices: 134, compliance: 90 },
            { department: 'HR', devices: 98, compliance: 97 },
            { department: 'Finance', devices: 87, compliance: 94 },
            { department: 'Operations', devices: 338, compliance: 85 }
          ],
          recentActivity: [
            { date: '2024-01-15', enrolled: 12, removed: 2 },
            { date: '2024-01-14', enrolled: 8, removed: 1 },
            { date: '2024-01-13', enrolled: 15, removed: 3 },
            { date: '2024-01-12', enrolled: 10, removed: 0 },
            { date: '2024-01-11', enrolled: 7, removed: 1 },
            { date: '2024-01-10', enrolled: 13, removed: 2 },
            { date: '2024-01-09', enrolled: 9, removed: 1 }
          ]
        };

      case 'compliance-status':
        return {
          type: 'compliance-status',
          overallCompliance: 82.1,
          complianceByPolicy: [
            { policy: 'Password Policy', compliant: 1156, nonCompliant: 91, complianceRate: 92.7 },
            { policy: 'Encryption Policy', compliant: 1089, nonCompliant: 158, complianceRate: 87.3 },
            { policy: 'Screen Lock Policy', compliant: 1201, nonCompliant: 46, complianceRate: 96.3 },
            { policy: 'App Whitelist', compliant: 1023, nonCompliant: 224, complianceRate: 82.0 },
            { policy: 'OS Updates', compliant: 987, nonCompliant: 260, complianceRate: 79.1 },
            { policy: 'Antivirus Status', compliant: 1134, nonCompliant: 113, complianceRate: 90.9 }
          ],
          complianceByPlatform: [
            { platform: 'Windows', total: 542, compliant: 467, rate: 86.2 },
            { platform: 'Linux', total: 312, compliant: 289, rate: 92.6 },
            { platform: 'macOS', total: 198, compliant: 185, rate: 93.4 },
            { platform: 'Android', total: 145, compliant: 132, rate: 91.0 },
            { platform: 'iOS', total: 50, compliant: 48, rate: 96.0 }
          ],
          complianceTrend: [
            { month: 'Jul', rate: 78.5 },
            { month: 'Aug', rate: 79.2 },
            { month: 'Sep', rate: 80.1 },
            { month: 'Oct', rate: 80.8 },
            { month: 'Nov', rate: 81.3 },
            { month: 'Dec', rate: 81.9 },
            { month: 'Jan', rate: 82.1 }
          ],
          violations: [
            { severity: 'Critical', count: 45 },
            { severity: 'High', count: 89 },
            { severity: 'Medium', count: 156 },
            { severity: 'Low', count: 234 }
          ]
        };

      case 'platform-distribution':
        return {
          type: 'platform-distribution',
          platformCounts: [
            { platform: 'Windows', count: 542, percentage: 43.5, color: '#0078D4' },
            { platform: 'Linux', count: 312, percentage: 25.0, color: '#FCC624' },
            { platform: 'macOS', count: 198, percentage: 15.9, color: '#000000' },
            { platform: 'Android', count: 145, percentage: 11.6, color: '#3DDC84' },
            { platform: 'iOS', count: 50, percentage: 4.0, color: '#000000' }
          ],
          osVersions: {
            windows: [
              { version: 'Windows 11', count: 312 },
              { version: 'Windows 10', count: 198 },
              { version: 'Windows Server 2022', count: 32 }
            ],
            linux: [
              { version: 'Ubuntu 22.04', count: 145 },
              { version: 'Ubuntu 20.04', count: 98 },
              { version: 'RHEL 9', count: 45 },
              { version: 'Debian 12', count: 24 }
            ],
            macos: [
              { version: 'macOS Sonoma', count: 98 },
              { version: 'macOS Ventura', count: 67 },
              { version: 'macOS Monterey', count: 33 }
            ],
            android: [
              { version: 'Android 14', count: 67 },
              { version: 'Android 13', count: 54 },
              { version: 'Android 12', count: 24 }
            ],
            ios: [
              { version: 'iOS 17', count: 28 },
              { version: 'iOS 16', count: 22 }
            ]
          },
          deviceTypes: [
            { type: 'Desktop', count: 856 },
            { type: 'Laptop', count: 234 },
            { type: 'Mobile', count: 145 },
            { type: 'Tablet', count: 12 }
          ],
          growthTrend: [
            { month: 'Jul', windows: 520, linux: 298, macos: 185, android: 140, ios: 48 },
            { month: 'Aug', windows: 525, linux: 302, macos: 189, android: 142, ios: 49 },
            { month: 'Sep', windows: 530, linux: 305, macos: 192, android: 143, ios: 49 },
            { month: 'Oct', windows: 535, linux: 308, macos: 195, android: 144, ios: 50 },
            { month: 'Nov', windows: 538, linux: 310, macos: 196, android: 144, ios: 50 },
            { month: 'Dec', windows: 540, linux: 311, macos: 197, android: 145, ios: 50 },
            { month: 'Jan', windows: 542, linux: 312, macos: 198, android: 145, ios: 50 }
          ]
        };

      case 'security-alerts':
        return {
          type: 'security-alerts',
          totalAlerts: 524,
          criticalAlerts: 45,
          highAlerts: 89,
          mediumAlerts: 156,
          lowAlerts: 234,
          alertTypes: [
            { type: 'Failed Login Attempts', count: 156, severity: 'High' },
            { type: 'Unpatched Vulnerabilities', count: 134, severity: 'Critical' },
            { type: 'Malware Detected', count: 89, severity: 'Critical' },
            { type: 'Unauthorized Access', count: 67, severity: 'High' },
            { type: 'Policy Violations', count: 45, severity: 'Medium' },
            { type: 'Suspicious Activity', count: 33, severity: 'Medium' }
          ],
          alertsByPlatform: [
            { platform: 'Windows', alerts: 234, critical: 28, high: 45, medium: 78, low: 83 },
            { platform: 'Linux', alerts: 145, critical: 12, high: 23, medium: 45, low: 65 },
            { platform: 'macOS', alerts: 67, critical: 3, high: 12, medium: 18, low: 34 },
            { platform: 'Android', alerts: 56, critical: 2, high: 7, medium: 12, low: 35 },
            { platform: 'iOS', alerts: 22, critical: 0, high: 2, medium: 3, low: 17 }
          ],
          alertTrend: [
            { date: '2024-01-09', count: 45 },
            { date: '2024-01-10', count: 52 },
            { date: '2024-01-11', count: 38 },
            { date: '2024-01-12', count: 61 },
            { date: '2024-01-13', count: 48 },
            { date: '2024-01-14', count: 55 },
            { date: '2024-01-15', count: 42 }
          ],
          topAffectedDevices: [
            { device: 'WIN-DESK-001', alerts: 23, severity: 'Critical' },
            { device: 'LIN-SRV-045', alerts: 18, severity: 'High' },
            { device: 'WIN-LAP-089', alerts: 15, severity: 'High' },
            { device: 'MAC-PRO-012', alerts: 12, severity: 'Medium' },
            { device: 'WIN-DESK-156', alerts: 11, severity: 'Medium' }
          ]
        };

      case 'deployment-status':
        return {
          type: 'deployment-status',
          totalDeployments: 156,
          activeDeployments: 23,
          completedDeployments: 118,
          failedDeployments: 15,
          deploymentByType: [
            { type: 'Application', total: 89, success: 78, failed: 11 },
            { type: 'Policy', total: 45, success: 32, failed: 13 },
            { type: 'Script', total: 22, success: 18, failed: 4 }
          ],
          deploymentByStatus: [
            { status: 'Completed', count: 118, color: '#10B981' },
            { status: 'In Progress', count: 23, color: '#3B82F6' },
            { status: 'Failed', count: 15, color: '#EF4444' }
          ],
          recentDeployments: [
            { name: 'Chrome Browser Update', target: 'Windows Devices', status: 'Completed', progress: 100, date: '2024-01-15' },
            { name: 'Security Policy Update', target: 'All Devices', status: 'In Progress', progress: 67, date: '2024-01-15' },
            { name: 'VS Code Installation', target: 'Engineering Dept', status: 'Completed', progress: 100, date: '2024-01-14' },
            { name: 'Firewall Rule Update', target: 'Linux Servers', status: 'Failed', progress: 0, date: '2024-01-14' },
            { name: 'Slack Deployment', target: 'macOS Devices', status: 'Completed', progress: 100, date: '2024-01-13' },
            { name: 'Antivirus Update', target: 'All Windows', status: 'In Progress', progress: 45, date: '2024-01-13' }
          ],
          deploymentTrend: [
            { week: 'Week 1', applications: 12, policies: 8, scripts: 3 },
            { week: 'Week 2', applications: 15, policies: 10, scripts: 4 },
            { week: 'Week 3', applications: 18, policies: 12, scripts: 5 },
            { week: 'Week 4', applications: 14, policies: 9, scripts: 3 }
          ],
          successRate: 88.5
        };

      case 'hardware-inventory':
        return {
          type: 'hardware-inventory',
          summary: {
            devicesCovered: 3,
            avgMemoryGb: 16,
            avgStorageGb: 512,
          },
          rows: [
            {
              deviceId: 'WIN-DESK-001',
              model: 'Dell OptiPlex 7050',
              osVersion: 'Windows 11',
              cpu: 'Intel i7',
              memoryGb: 16,
              storageGb: 512,
              batteryHealth: 'N/A',
              lastSync: new Date().toISOString(),
              collectedBy: 'mock',
            },
            {
              deviceId: 'MAC-LAP-014',
              model: 'MacBook Pro 14"',
              osVersion: 'macOS Sonoma',
              cpu: 'Apple M3',
              memoryGb: 16,
              storageGb: 1024,
              batteryHealth: 'Good',
              lastSync: new Date().toISOString(),
              collectedBy: 'mock',
            },
            {
              deviceId: 'LIN-SRV-018',
              model: 'Lenovo ThinkSystem',
              osVersion: 'Ubuntu 22.04',
              cpu: 'AMD EPYC',
              memoryGb: 32,
              storageGb: 2048,
              batteryHealth: 'N/A',
              lastSync: new Date().toISOString(),
              collectedBy: 'mock',
            },
          ],
        };

      case 'software-inventory':
        return {
          type: 'software-inventory',
          summary: {
            applications: 3,
            totalFootprintMb: 1390,
            meteredApps: 2,
          },
          rows: [
            {
              deviceId: 'WIN-DESK-001',
              owner: 'it@datail.in',
              name: 'Microsoft Edge',
              version: '121.0',
              publisher: 'Microsoft',
              sizeMb: 350,
              metered: false,
              lastSync: new Date().toISOString(),
            },
            {
              deviceId: 'MAC-LAP-014',
              owner: 'engineering@datail.in',
              name: 'Xcode',
              version: '15.2',
              publisher: 'Apple',
              sizeMb: 900,
              metered: false,
              lastSync: new Date().toISOString(),
            },
            {
              deviceId: 'LIN-SRV-018',
              owner: 'ops@datail.in',
              name: 'Docker',
              version: '24.0',
              publisher: 'Docker Inc.',
              sizeMb: 140,
              metered: true,
              lastSync: new Date().toISOString(),
            },
          ],
        };

      case 'software-metering':
        return {
          type: 'software-metering',
          summary: {
            trackedDevices: 3,
            totalUsageMinutes: 1200,
          },
          rows: [
            {
              deviceId: 'WIN-DESK-001',
              application: 'Teams',
              usageMinutes: 480,
              lastMetering: new Date().toISOString(),
            },
            {
              deviceId: 'MAC-LAP-014',
              application: 'Xcode',
              usageMinutes: 360,
              lastMetering: new Date().toISOString(),
            },
            {
              deviceId: 'LIN-SRV-018',
              application: 'Docker',
              usageMinutes: 360,
              lastMetering: new Date().toISOString(),
            },
          ],
        };

      case 'activity-tracking':
        return {
          type: 'activity-tracking',
          summary: {
            unique_users: 0,
            unique_devices: 0,
            total_activities: 0,
            total_time_seconds: 0,
            productive_time_seconds: 0,
            unique_domains: 0,
            unique_apps: 0,
          },
          activities: [],
          top_apps: [],
          top_urls: [],
          unique_users: [],
          time_range: {
            start: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            end: new Date().toISOString(),
            hours: 24,
          },
        };

      default:
        return null;
    }
  }, [reportId]);

  const [reportData, setReportData] = useState<ReportData | null>(fallbackData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [syncScope, setSyncScope] = useState<string[]>(
    reportId === 'software-metering' ? ['software'] : ['hardware', 'software']
  );
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [syncNote, setSyncNote] = useState('');

  useEffect(() => {
    setReportData(fallbackData);
  }, [fallbackData]);

  useEffect(() => {
    if (reportId === 'software-metering') {
      setSyncScope(['software']);
    } else {
      setSyncScope(['hardware', 'software']);
    }
  }, [reportId]);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let payload = null;
      let response = null;
      
      // Use real endpoints for each report type
      switch (reportId) {
        case 'device-summary':
          response = await apiService.getDeviceSummaryAnalytics();
          break;
        case 'platform-distribution':
          response = await apiService.getPlatformDistributionAnalytics();
          break;
        case 'compliance-status':
          response = await apiService.getComplianceStatusAnalytics();
          break;
        case 'hardware-inventory':
          response = await apiService.getHardwareInventoryAnalytics(0, 100);
          break;
        case 'software-inventory':
          response = await apiService.getSoftwareInventoryAnalytics(0, 100);
          break;
        case 'activity-tracking':
          response = await apiService.getActivityTrackingAnalytics(
            userFilter !== 'all' ? userFilter : undefined,
            deviceFilter !== 'all' ? deviceFilter : undefined,
            hoursFilter,
            0,
            100
          );
          break;
        default:
          // For other reports, try the existing method
          payload = await apiService.getBuntuAnalyticsReport(reportId);
          break;
      }
      
      if (response && response.success && response.data) {
        payload = response.data;
        // For activity-tracking, ensure the type is set
        if (reportId === 'activity-tracking' && payload) {
          payload.type = 'activity-tracking';
          // Ensure all required fields exist with defaults
          const fallback = isActivityTrackingData(fallbackData) ? fallbackData : null;
          if (!payload.summary) payload.summary = fallback?.summary || {
            unique_users: 0,
            unique_devices: 0,
            total_activities: 0,
            total_time_seconds: 0,
            productive_time_seconds: 0,
            unique_domains: 0,
            unique_apps: 0
          };
          if (!payload.activities) payload.activities = [];
          if (!payload.top_apps) payload.top_apps = [];
          if (!payload.top_urls) payload.top_urls = [];
          if (!payload.unique_users) payload.unique_users = [];
          if (!payload.time_range) payload.time_range = fallback?.time_range || {
            start: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            end: new Date().toISOString(),
            hours: 24
          };
        }
        console.log(`[Analytics] Loaded real data for ${reportId}:`, payload);
      } else {
        console.warn(`[Analytics] No data in response for ${reportId}:`, response);
      }
      
      if (payload) {
        // Transform department distribution to use readable names
        if (reportId === 'device-summary' && payload.departmentDistribution) {
          try {
            const customCollections = JSON.parse(localStorage.getItem('buntu_custom_collections') || '[]');
            const collectionMap = new Map(customCollections.map((c: any) => [c.id, c.name]));
            
            payload.departmentDistribution = payload.departmentDistribution.map((dept: any) => {
              const deptId = dept.department;
              
              // Check if it's a custom collection and use its stored name
              if (collectionMap.has(deptId)) {
                return { ...dept, department: collectionMap.get(deptId) };
              }
              
              // If it's a custom collection ID but missing from localStorage, use a generic name
              if (deptId && deptId.startsWith('dept-custom-')) {
                return { ...dept, department: 'Unnamed Collection' };
              }
              
              // For standard departments, format the ID (remove 'dept-' prefix and format)
              if (deptId && deptId.startsWith('dept-')) {
                const name = deptId.replace(/^dept-/, '').split('-').map((word: string) => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');
                return { ...dept, department: name || deptId };
              }
              
              return dept;
            });
          } catch (err) {
            console.warn('Failed to transform department names:', err);
          }
        }
        
        setReportData(payload);
        setError(null); // Clear any previous errors
      } else {
        console.warn(`[Analytics] Using fallback data for ${reportId}`);
        setReportData(fallbackData);
        setError('Live analytics data unavailable. Showing cached snapshot.');
      }
    } catch (err: any) {
      console.error(`[Analytics] Failed to load report ${reportId}:`, err);
      console.error(`[Analytics] Error details:`, err.message, err.stack);
      setReportData(fallbackData);
      setError(`Live analytics data unavailable: ${err.message || 'Network error'}. Showing cached snapshot.`);
    } finally {
      setLoading(false);
    }
  }, [reportId, fallbackData]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData, userFilter, deviceFilter, hoursFilter]);

  const ensureDevicesLoaded = useCallback(async () => {
    if (devicesLoaded) return;
    try {
      let list: any[] = await apiService.getBuntuMDMDevices();
      if (!Array.isArray(list)) list = [];
      // Normalize: Buntu MDM may return id or device_id; ensure consistent id and readable label
      const normalized = list.map((d: any) => ({
        ...d,
        id: d.id ?? d.device_id ?? d.serial_number ?? String(d),
        name: d.name ?? d.hostname ?? d.device_name ?? d.model ?? d.id ?? d.device_id ?? 'Unknown device',
        model: d.model ?? d.model_name,
        os_version: d.os_version ?? d.osVersion,
      }));
      if (normalized.length === 0) {
        // Fallback: use main UEM Hub devices so the dropdown is usable (sync may work if backend links them)
        const hubDevices = await apiService.getDevices();
        const hubList = Array.isArray(hubDevices) ? hubDevices : [];
        const fromHub = hubList.map((d: any) => ({
          ...d,
          id: d.device_id ?? d.id,
          name: d.name ?? d.hostname ?? d.device_metadata?.hostname ?? d.device_id ?? 'Unknown device',
          model: d.device_metadata?.model,
          os_version: d.os,
        })).filter((d: any) => d.id);
        setDevices(fromHub);
      } else {
        setDevices(normalized);
      }
    } catch (err) {
      console.error('Failed to load devices for sync', err);
      setDevices([]);
    } finally {
      setDevicesLoaded(true);
    }
  }, [devicesLoaded]);

  const fetchSyncHistory = useCallback(async (deviceId: string) => {
    if (!deviceId) {
      setSyncHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const history = await apiService.getDeviceSyncHistory(deviceId, 10);
      setSyncHistory(Array.isArray(history) ? history : []);
    } catch (err) {
      console.error('Failed to load sync history', err);
      setSyncHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      fetchSyncHistory(selectedDevice);
    } else {
      setSyncHistory([]);
    }
  }, [selectedDevice, fetchSyncHistory]);

  const handleScopeToggle = (scopeId: string) => {
    setSyncScope(prev => {
      if (reportId === 'software-metering') {
        return ['software'];
      }
      if (prev.includes(scopeId)) {
        return prev.filter(item => item !== scopeId);
      }
      return [...prev, scopeId];
    });
  };

  const handleExport = () => {
    const payload = JSON.stringify(reportData ?? fallbackData ?? {}, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportId}-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const openSyncModal = async () => {
    await ensureDevicesLoaded();
    setSyncModalOpen(true);
    setSyncMessage(null);
  };

  const closeSyncModal = () => {
    setSyncModalOpen(false);
    setSelectedDevice('');
    setSyncHistory([]);
    setSyncMessage(null);
    setSyncNote('');
  };

  const handleSyncNow = async () => {
    if (!selectedDevice) {
      setSyncMessage({ type: 'error', text: 'Select a device to sync.' });
      return;
    }
    const scopesToUse = syncScope.length ? syncScope : ['software'];
    const noteToSend = syncNote?.trim() || `Manual sync initiated from ${reportName}`;
    setSyncLoading(true);
    setSyncMessage(null);
    try {
      await apiService.triggerDeviceInventorySync(
        selectedDevice,
        scopesToUse,
        noteToSend
      );
      setSyncMessage({ type: 'success', text: 'Sync triggered successfully.' });
      fetchSyncHistory(selectedDevice);
      fetchReportData();
    } catch (err: any) {
      const msg = err?.message || '';
      const text =
        msg.includes('Failed to fetch') || (err?.name === 'TypeError' && msg.toLowerCase().includes('fetch'))
          ? 'Sync request could not reach the server. Check that the UEM backend is running and reachable.'
          : msg || 'Failed to trigger sync. Please try again.';
      setSyncMessage({ type: 'error', text });
    } finally {
      setSyncLoading(false);
    }
  };

  if (!reportData) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <p className="text-slate-500">Report data not available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{reportName}</h1>
              <p className="text-slate-600 mt-1">{description}</p>
            </div>
            <div className="flex items-center gap-3">
              {['hardware-inventory', 'software-inventory', 'software-metering'].includes(reportId) && (
                <button
                  onClick={openSyncModal}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md text-sm font-semibold"
                >
                  <RefreshCcw size={16} />
                  Sync Data
                </button>
              )}
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                <Download size={18} />
                Export Report
              </button>
            </div>
          </div>
          {(loading || error) && (
            <div className="mt-4">
              {loading && (
                <div className="bg-white border border-slate-200 rounded-lg p-4 text-slate-500 text-sm shadow-sm">
                  Loading latest analytics data...
                </div>
              )}
              {!loading && error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm shadow-sm">
                  {error}
                </div>
              )}
            </div>
          )}
          {reportData && (reportData as any).metadata && (
            <p className="text-xs text-slate-500 mt-2">
              Last generated: {(reportData as any).metadata.generatedAt || 'N/A'}
            </p>
          )}
        </div>

        {/* Report Content */}
        {isDeviceSummaryData(reportData) && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Devices</span>
                  <Monitor size={20} className="text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.summary.totalDevices.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Active Devices</span>
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div className="text-3xl font-bold text-green-600">{reportData.summary.activeDevices.toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">{((reportData.summary.activeDevices / reportData.summary.totalDevices) * 100).toFixed(1)}% online</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Laptops</span>
                  <Laptop size={20} className="text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-purple-600">{reportData.summary.laptops.toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.totalDevices > 0 ? ((reportData.summary.laptops / reportData.summary.totalDevices) * 100).toFixed(1) : '0.0'}% of total</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Desktops</span>
                  <Monitor size={20} className="text-indigo-600" />
                </div>
                <div className="text-3xl font-bold text-indigo-600">{reportData.summary.desktops.toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.totalDevices > 0 ? ((reportData.summary.desktops / reportData.summary.totalDevices) * 100).toFixed(1) : '0.0'}% of total</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">VMs</span>
                  <Server size={20} className="text-cyan-600" />
                </div>
                <div className="text-3xl font-bold text-cyan-600">{(reportData.summary.vms || 0).toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.totalDevices > 0 ? (((reportData.summary.vms || 0) / reportData.summary.totalDevices) * 100).toFixed(1) : '0.0'}% of total</div>
              </div>
            </div>

            {/* Platform Distribution */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Platform Distribution</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData.platformDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: { name?: string; percent?: number }) =>
                        `${name}: ${(((percent ?? 0) * 100)).toFixed(1)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {reportData.platformDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {reportData.platformDistribution.map((platform, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: platform.color }}></div>
                        <span className="font-medium text-slate-700">{platform.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">{platform.value}</div>
                        <div className="text-xs text-slate-500">{((platform.value / reportData.summary.totalDevices) * 100).toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Department Distribution */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Devices by Department</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.departmentDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="devices" fill="#3B82F6" name="Total Devices" />
                  <Bar dataKey="compliance" fill="#10B981" name="Compliant" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Activity (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={reportData.recentActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="enrolled" stroke="#10B981" strokeWidth={2} name="Enrolled" />
                  <Line type="monotone" dataKey="removed" stroke="#EF4444" strokeWidth={2} name="Removed" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {isComplianceStatusData(reportData) && (
          <div className="space-y-6">
            {/* Overall Compliance */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Overall Compliance Rate</h3>
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-48">
                  <svg className="transform -rotate-90 w-48 h-48">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#E5E7EB"
                      strokeWidth="16"
                      fill="none"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#10B981"
                      strokeWidth="16"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 88 * (reportData.overallCompliance / 100)} ${2 * Math.PI * 88}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-slate-900">{reportData.overallCompliance}%</div>
                      <div className="text-sm text-slate-500">Compliant</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance by Policy */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Compliance by Policy</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={reportData.complianceByPolicy} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="policy" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="compliant" fill="#10B981" name="Compliant" />
                  <Bar dataKey="nonCompliant" fill="#EF4444" name="Non-Compliant" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Compliance by Platform */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Compliance Rate by Platform</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.complianceByPlatform}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="platform" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="rate" fill="#3B82F6" name="Compliance Rate (%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Compliance Trend */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Compliance Trend (Last 7 Months)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.complianceTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="rate" stroke="#3B82F6" strokeWidth={3} name="Compliance Rate (%)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Violations by Severity */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Violations by Severity</h3>
              <div className="grid grid-cols-4 gap-4">
                {reportData.violations.map((violation, idx) => {
                  const colors = {
                    'Critical': '#EF4444',
                    'High': '#F59E0B',
                    'Medium': '#F97316',
                    'Low': '#6B7280'
                  };
                  return (
                    <div key={idx} className="text-center p-4 bg-slate-50 rounded-lg">
                      <div className="text-3xl font-bold" style={{ color: colors[violation.severity as keyof typeof colors] }}>
                        {violation.count}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">{violation.severity}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {isPlatformDistributionData(reportData) && (
          <div className="space-y-6">
            {/* Platform Counts */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Platform Distribution</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData.platformCounts}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ payload }: { payload?: { platform: string; percentage: number } }) =>
                        payload ? `${payload.platform}: ${payload.percentage}%` : ''
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {reportData.platformCounts.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {reportData.platformCounts.map((platform, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded" style={{ backgroundColor: platform.color }}></div>
                        <span className="font-medium text-slate-700">{platform.platform}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">{platform.count}</div>
                        <div className="text-xs text-slate-500">{platform.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* OS Versions */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">OS Version Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(reportData.osVersions).map(([platform, versions]) => (
                  <div key={platform} className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-3 capitalize">{platform}</h4>
                    <div className="space-y-2">
                      {versions.map((v: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{v.version}</span>
                          <span className="font-medium text-slate-900">{v.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Device Types */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Device Types</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.deviceTypes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Growth Trend */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Platform Growth Trend (Last 7 Months)</h3>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={reportData.growthTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="windows" stroke="#0078D4" strokeWidth={2} name="Windows" />
                  <Line type="monotone" dataKey="linux" stroke="#FCC624" strokeWidth={2} name="Linux" />
                  <Line type="monotone" dataKey="macos" stroke="#000000" strokeWidth={2} name="macOS" />
                  <Line type="monotone" dataKey="android" stroke="#3DDC84" strokeWidth={2} name="Android" />
                  <Line type="monotone" dataKey="ios" stroke="#6B7280" strokeWidth={2} name="iOS" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {isSecurityAlertsData(reportData) && (
          <div className="space-y-6">
            {/* Alert Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Alerts</span>
                  <AlertTriangle size={20} className="text-orange-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.totalAlerts}</div>
              </div>
              <div className="bg-white rounded-lg border border-red-200 p-6 shadow-sm bg-red-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-red-700">Critical</span>
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div className="text-3xl font-bold text-red-600">{reportData.criticalAlerts}</div>
              </div>
              <div className="bg-white rounded-lg border border-orange-200 p-6 shadow-sm bg-orange-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-orange-700">High</span>
                  <AlertTriangle size={20} className="text-orange-600" />
                </div>
                <div className="text-3xl font-bold text-orange-600">{reportData.highAlerts}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Medium/Low</span>
                  <AlertTriangle size={20} className="text-slate-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.mediumAlerts + reportData.lowAlerts}</div>
              </div>
            </div>

            {/* Alert Types */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Alerts by Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.alertTypes} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="type" type="category" width={180} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Alerts by Platform */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Alerts by Platform</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={reportData.alertsByPlatform}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="platform" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="critical" stackId="a" fill="#EF4444" name="Critical" />
                  <Bar dataKey="high" stackId="a" fill="#F59E0B" name="High" />
                  <Bar dataKey="medium" stackId="a" fill="#F97316" name="Medium" />
                  <Bar dataKey="low" stackId="a" fill="#6B7280" name="Low" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Alert Trend */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Alert Trend (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.alertTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#EF4444" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Top Affected Devices */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Top Affected Devices</h3>
              <div className="space-y-3">
                {reportData.topAffectedDevices.map((device, idx) => {
                  const severityColors = {
                    'Critical': 'bg-red-100 text-red-800 border-red-200',
                    'High': 'bg-orange-100 text-orange-800 border-orange-200',
                    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  };
                  return (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Monitor size={20} className="text-slate-400" />
                        <span className="font-medium text-slate-700">{device.device}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${severityColors[device.severity as keyof typeof severityColors]}`}>
                          {device.severity}
                        </span>
                        <span className="font-bold text-slate-900">{device.alerts} alerts</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {isDeploymentStatusData(reportData) && (
          <div className="space-y-6">
            {/* Deployment Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Deployments</span>
                  <TrendingUp size={20} className="text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.totalDeployments}</div>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-6 shadow-sm bg-green-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-green-700">Completed</span>
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div className="text-3xl font-bold text-green-600">{reportData.completedDeployments}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.successRate}% success rate</div>
              </div>
              <div className="bg-white rounded-lg border border-blue-200 p-6 shadow-sm bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-blue-700">In Progress</span>
                  <Clock size={20} className="text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-blue-600">{reportData.activeDeployments}</div>
              </div>
              <div className="bg-white rounded-lg border border-red-200 p-6 shadow-sm bg-red-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-red-700">Failed</span>
                  <XCircle size={20} className="text-red-600" />
                </div>
                <div className="text-3xl font-bold text-red-600">{reportData.failedDeployments}</div>
              </div>
            </div>

            {/* Deployment Status Pie */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Deployment Status Overview</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reportData.deploymentByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                      label={({ payload }: { payload?: { status: string; count: number } }) =>
                        payload ? `${payload.status}: ${payload.count}` : ''
                      }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {reportData.deploymentByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Deployment by Type */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Deployments by Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.deploymentByType}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="success" fill="#10B981" name="Successful" />
                  <Bar dataKey="failed" fill="#EF4444" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent Deployments */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Deployments</h3>
              <div className="space-y-3">
                {reportData.recentDeployments.map((deployment, idx) => {
                  const statusColors = {
                    'Completed': 'bg-green-100 text-green-800 border-green-200',
                    'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
                    'Failed': 'bg-red-100 text-red-800 border-red-200'
                  };
                  return (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-slate-800">{deployment.name}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${statusColors[deployment.status as keyof typeof statusColors]}`}>
                            {deployment.status}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600">
                          Target: {deployment.target} • {deployment.date}
                        </div>
                        {deployment.status === 'In Progress' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>Progress</span>
                              <span>{deployment.progress}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${deployment.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Deployment Trend */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Deployment Trend (Last 4 Weeks)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.deploymentTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="applications" fill="#3B82F6" name="Applications" />
                  <Bar dataKey="policies" fill="#10B981" name="Policies" />
                  <Bar dataKey="scripts" fill="#F59E0B" name="Scripts" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {isHardwareInventoryData(reportData) && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Devices Covered</span>
                  <Monitor size={18} className="text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.summary.devicesCovered}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Avg Memory</span>
                  <Cpu size={18} className="text-indigo-600" />
                </div>
                <div className="text-3xl font-bold text-indigo-600">
                  {reportData.summary.avgMemoryGb.toFixed(1)} GB
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Avg Storage</span>
                  <HardDrive size={18} className="text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-emerald-600">
                  {reportData.summary.avgStorageGb.toFixed(1)} GB
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">Latest Hardware Inventory</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{reportData.rows.length} records</span>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-slate-700"
                    >
                      <Filter size={14} />
                      Filters
                    </button>
                  </div>
                </div>
                
                {/* Filter Bar */}
                {showFilters && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search devices..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <select
                        value={platformFilter}
                        onChange={(e) => setPlatformFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                      >
                        <option value="all">All Platforms</option>
                        <option value="windows">Windows</option>
                        <option value="linux">Linux</option>
                        <option value="macos">macOS</option>
                        <option value="android">Android</option>
                        <option value="ios">iOS</option>
                      </select>
                      <select
                        value={osFilter}
                        onChange={(e) => setOsFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                      >
                        <option value="all">All OS Versions</option>
                        <option value="Windows 11">Windows 11</option>
                        <option value="Windows 10">Windows 10</option>
                        <option value="Ubuntu">Ubuntu</option>
                        <option value="macOS">macOS</option>
                      </select>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="offline">Offline</option>
                        <option value="warning">Warning</option>
                      </select>
                      <button
                        onClick={() => {
                          setPlatformFilter('all');
                          setOsFilter('all');
                          setStatusFilter('all');
                          setSearchQuery('');
                        }}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors text-slate-700 bg-white"
                      >
                        <X size={14} />
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Device</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Serial Number</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Model / OS</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">CPU</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Memory</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Storage</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Battery</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Last Sync</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(() => {
                      // Filter rows based on filters
                      const filteredRows = reportData.rows.filter(row => {
                        // Search filter
                        if (searchQuery) {
                          const query = searchQuery.toLowerCase();
                          const matchesSearch = 
                            row.deviceId?.toLowerCase().includes(query) ||
                            row.deviceName?.toLowerCase().includes(query) ||
                            row.serialNumber?.toLowerCase().includes(query) ||
                            row.model?.toLowerCase().includes(query) ||
                            row.osVersion?.toLowerCase().includes(query) ||
                            row.cpu?.toLowerCase().includes(query);
                          if (!matchesSearch) return false;
                        }
                        
                        // Platform filter (based on OS version)
                        if (platformFilter !== 'all') {
                          const osLower = (row.osVersion || '').toLowerCase();
                          const platformLower = platformFilter.toLowerCase();
                          if (platformLower === 'windows' && !osLower.includes('windows')) return false;
                          if (platformLower === 'linux' && !osLower.includes('linux') && !osLower.includes('ubuntu')) return false;
                          if (platformLower === 'macos' && !osLower.includes('mac') && !osLower.includes('darwin')) return false;
                          if (platformLower === 'android' && !osLower.includes('android')) return false;
                          if (platformLower === 'ios' && !osLower.includes('ios')) return false;
                        }
                        
                        // OS version filter
                        if (osFilter !== 'all' && row.osVersion) {
                          if (!row.osVersion.toLowerCase().includes(osFilter.toLowerCase())) return false;
                        }
                        
                        return true;
                      });
                      
                      if (filteredRows.length === 0) {
                        return (
                          <tr>
                            <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                              No hardware inventory matches the selected filters.
                            </td>
                          </tr>
                        );
                      }
                      
                      return filteredRows.map(row => (
                        <tr key={row.deviceId} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{row.deviceName || row.deviceId}</td>
                          <td className="px-4 py-3 text-slate-600">{row.serialNumber || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">
                            <div className="font-semibold">{row.model || 'Unknown Model'}</div>
                            <div className="text-xs text-slate-500">{row.osVersion || 'Unknown OS'}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{row.cpu || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{row.memoryGb ? `${row.memoryGb} GB` : '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{row.storageGb ? `${row.storageGb} GB` : '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{row.batteryHealth || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {row.lastSync ? new Date(row.lastSync).toLocaleString() : '—'}
                            <div className="text-xs text-slate-400">{row.collectedBy ? `by ${row.collectedBy}` : ''}</div>
                          </td>
                        </tr>
                      ));
                    })() as React.ReactNode}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {isSoftwareInventoryData(reportData) && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Applications</span>
                  <Server size={18} className="text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{reportData.summary.applications}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Metered Apps</span>
                  <PieChartIcon size={18} className="text-rose-600" />
                </div>
                <div className="text-3xl font-bold text-rose-600">{reportData.summary.meteredApps}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Footprint</span>
                  <Database size={18} className="text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-emerald-600">
                  {reportData.summary.totalFootprintMb.toLocaleString()} MB
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Software Inventory</h3>
                <span className="text-xs text-slate-500">{reportData.rows.length} entries</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Application</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Device</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Serial Number</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Publisher</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Size</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Metered</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Last Sync</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                          No software inventory captured yet.
                        </td>
                      </tr>
                    )}
                    {reportData.rows.map((row, idx) => (
                      <tr key={`${row.deviceId}-${row.name}-${idx}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">
                          <div>{row.name}</div>
                          <div className="text-xs text-slate-500">{row.version || 'Version N/A'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          <div className="font-semibold">{row.deviceName || row.deviceId}</div>
                          <div className="text-xs text-slate-500">{row.owner || 'Unknown Owner'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{row.serialNumber || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.publisher || 'Unknown'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.sizeMb ? `${row.sizeMb} MB` : '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${row.metered ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {row.metered ? 'Tracked' : 'Untracked'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.lastSync ? new Date(row.lastSync).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {isSoftwareMeteringData(reportData) && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Tracked Devices</span>
                  <Tablet size={18} className="text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-blue-600">{reportData.summary.trackedDevices}</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Usage</span>
                  <Activity size={18} className="text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {reportData.summary.totalUsageMinutes.toLocaleString()} min
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Software Metering</h3>
                <span className="text-xs text-slate-500">{reportData.rows.length} device-application pairs</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Device</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Serial Number</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Application</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Usage (min)</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Last Metering</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.rows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                          No metering data yet.
                        </td>
                      </tr>
                    )}
                    {reportData.rows.map((row, idx) => (
                      <tr key={`${row.deviceId}-${row.application}-${idx}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{row.deviceName || row.deviceId}</td>
                        <td className="px-4 py-3 text-slate-600">{row.serialNumber || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.application}</td>
                        <td className="px-4 py-3 text-slate-600">{row.usageMinutes.toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {row.lastMetering ? new Date(row.lastMetering).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {reportData && isActivityTrackingData(reportData) && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Filter by User</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={userFilter}
                    onChange={(e) => {
                      setUserFilter(e.target.value);
                      fetchReportData();
                    }}
                  >
                    <option value="all">All Users</option>
                    {reportData.unique_users.map((user) => (
                      <option key={user} value={user}>{user}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Time Range</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={hoursFilter}
                    onChange={(e) => {
                      setHoursFilter(Number(e.target.value));
                      fetchReportData();
                    }}
                  >
                    <option value={1}>Last 1 Hour</option>
                    <option value={8}>Last 8 Hours</option>
                    <option value={24}>Last 24 Hours</option>
                    <option value={168}>Last 7 Days</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchReportData}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                  >
                    <RefreshCcw size={16} className="inline mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Time</span>
                  <Clock size={18} className="text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {Math.floor(reportData.summary.total_time_seconds / 3600)}h {Math.floor((reportData.summary.total_time_seconds % 3600) / 60)}m
                </div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.total_activities} activities</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Productive Time</span>
                  <TrendingUp size={18} className="text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {Math.floor(reportData.summary.productive_time_seconds / 3600)}h {Math.floor((reportData.summary.productive_time_seconds % 3600) / 60)}m
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {reportData.summary.total_time_seconds > 0 
                    ? Math.round((reportData.summary.productive_time_seconds / reportData.summary.total_time_seconds) * 100)
                    : 0}% productive
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Unique Users</span>
                  <Users size={18} className="text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-purple-600">{reportData.summary.unique_users}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.unique_devices} devices</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Apps & URLs</span>
                  <Activity size={18} className="text-indigo-600" />
                </div>
                <div className="text-3xl font-bold text-indigo-600">{reportData.summary.unique_apps}</div>
                <div className="text-xs text-slate-500 mt-1">{reportData.summary.unique_domains} domains</div>
              </div>
            </div>

            {/* Top Apps and URLs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-lg font-semibold text-slate-800">Top Applications</h3>
                </div>
                <div className="p-4">
                  {reportData.top_apps.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No application data available</p>
                  ) : (
                    <div className="space-y-3">
                      {reportData.top_apps.map((app, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{app.app_name}</div>
                            <div className="text-xs text-slate-500">{app.activity_count} sessions</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-slate-900">
                              {Math.floor(app.total_time_seconds / 60)}m {app.total_time_seconds % 60}s
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-lg font-semibold text-slate-800">Top URLs/Domains</h3>
                </div>
                <div className="p-4">
                  {reportData.top_urls.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No URL data available</p>
                  ) : (
                    <div className="space-y-3">
                      {reportData.top_urls.map((url, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{url.domain}</div>
                            <div className="text-xs text-slate-500">
                              {url.category || 'Uncategorized'} • {url.activity_count} visits
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <div className="font-semibold text-slate-900">
                              {Math.floor(url.total_time_seconds / 60)}m {url.total_time_seconds % 60}s
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Activity Log Table */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Activity Log</h3>
                <span className="text-xs text-slate-500">{reportData.activities.length} activities</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Device</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Serial Number</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Application</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">URL/Domain</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Duration</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600 uppercase tracking-wider">Category</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.activities.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                          No activity data available. Activity tracking will appear here once agents start collecting data.
                        </td>
                      </tr>
                    )}
                    {reportData.activities.map((activity) => (
                      <tr key={activity.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">
                          {new Date(activity.start_time).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{activity.user_id || 'N/A'}</td>
                        <td className="px-4 py-3 text-slate-600">{activity.device_name || activity.device_id}</td>
                        <td className="px-4 py-3 text-slate-600">{activity.serial_number || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{activity.app_name}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <div className="max-w-xs truncate" title={activity.url || activity.domain || 'N/A'}>
                            {activity.url || activity.domain || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {Math.floor(activity.duration_seconds / 60)}m {activity.duration_seconds % 60}s
                        </td>
                        <td className="px-4 py-3">
                          {activity.is_productive !== undefined && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              activity.is_productive 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {activity.url_category || (activity.is_productive ? 'Productive' : 'Non-Productive')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Modal */}
      {syncModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Sync Inventory Data</h3>
                <p className="text-sm text-slate-500">Pull latest hardware/software inventory for a device</p>
                {selectedDevice && syncHistory.length > 0 && syncHistory[0]?.created_at && (
                  <p className="text-xs text-slate-400 mt-1" title="Most recent sync for selected device">
                    Last sync: {new Date(syncHistory[0].created_at).toLocaleString()}
                  </p>
                )}
              </div>
              <button onClick={closeSyncModal} className="text-slate-500 hover:text-slate-700">
                <XCircle size={24} />
              </button>
            </div>
            <div className="px-6 py-6 space-y-6 max-h-[80vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Device</label>
                <DevicePicker
                  devices={devices}
                  value={selectedDevice}
                  onChange={setSelectedDevice}
                  disabled={!devices.length}
                  emptyMessage="No devices available. Load inventory or ensure devices are enrolled."
                />
                {devices.length === 0 && devicesLoaded && (
                  <p className="text-xs text-amber-600 mt-1">No devices available. Load inventory or ensure devices are enrolled.</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Sync Scope</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={syncScope.includes('hardware')}
                        onChange={() => handleScopeToggle('hardware')}
                        disabled={reportId === 'software-metering'}
                      />
                      Hardware Inventory
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={syncScope.includes('software')}
                        onChange={() => handleScopeToggle('software')}
                      />
                      Software Inventory
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                  <textarea
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm h-24"
                    placeholder="Add context for this sync request..."
                    value={syncNote}
                    onChange={(e) => setSyncNote(e.target.value)}
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Software Inventory includes all installed applications. If you install Osquery remotely, run a sync here—once the agent reports back, Osquery will appear in the Software Inventory report and the system can identify that it is installed.
              </p>

              {syncMessage && (
                <div
                  className={`rounded-lg px-4 py-2 text-sm ${
                    syncMessage.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border border-rose-200'
                  }`}
                >
                  {syncMessage.text}
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  onClick={handleSyncNow}
                  disabled={syncLoading || !selectedDevice}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {syncLoading ? (
                    <span className="animate-spin border-2 border-white/60 border-t-transparent rounded-full w-4 h-4" />
                  ) : (
                    <RefreshCcw size={16} />
                  )}
                  {syncLoading ? 'Syncing...' : 'Sync Now'}
                </button>
                <div className="text-xs text-slate-500">
                  Scheduled syncs run every 72 hours automatically.
                </div>
              </div>

              <details className="bg-slate-50 rounded-xl border border-slate-200 group">
                <summary className="flex items-center justify-between cursor-pointer p-4 list-none [&::-webkit-details-marker]:hidden hover:bg-slate-100/50 rounded-xl transition-colors">
                  <div className="flex items-center gap-2">
                    <History size={18} className="text-slate-500" />
                    <h4 className="text-sm font-semibold text-slate-700">Sync History</h4>
                    {syncHistory.length > 0 && (
                      <span className="text-xs text-slate-400">({syncHistory.length} entries)</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                    onClick={(e) => {
                      e.preventDefault();
                      selectedDevice && fetchSyncHistory(selectedDevice);
                    }}
                  >
                    Refresh
                  </button>
                </summary>
                <div className="px-4 pb-4 pt-0">
                  {historyLoading ? (
                    <p className="text-sm text-slate-500">Loading history...</p>
                  ) : syncHistory.length === 0 ? (
                    <p className="text-sm text-slate-500">No sync history yet for this device.</p>
                  ) : (
                    <ul className="space-y-2 text-sm text-slate-600 max-h-48 overflow-y-auto">
                      {syncHistory.map((entry: any) => (
                        <li key={entry.command_id || entry.id} className="bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-800 capitalize">{entry.status || 'pending'}</span>
                            <span className="text-xs text-slate-400">
                              {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {Array.isArray(entry.scope) ? entry.scope.join(', ') : 'N/A'} • {entry.triggered_by || 'unknown'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultReportView;

